import { Formio } from 'formiojs';

const wrappedEditForms = new WeakSet();
const helpElements = new WeakMap();

// Follow WebformBuilder's registry/builderInfo discovery, excluding components
// without a user-facing runtime representation. Never wrap internal base classes.
function supportsDocumentation(type, ComponentClass) {
  const info = ComponentClass && ComponentClass.builderInfo;
  return !['hidden', 'unknown'].includes(type) && info && info.group && info.schema &&
    info.schema.type === type && typeof ComponentClass.editForm === 'function';
}

function findField(components, key) {
  for (const component of components || []) {
    if (component.key === key) return component;
    const nested = findField(component.components, key);
    if (nested) return nested;
  }
  return null;
}

export function installComponentDocumentation() {
  Object.entries(Formio.Components.components).forEach(([type, ComponentClass]) => {
    if (!supportsDocumentation(type, ComponentClass)) return;
    const original = ComponentClass.editForm;
    if (wrappedEditForms.has(original)) return;
    const editForm = function (...args) {
      const form = original.apply(this, args);
      if (!findField(form.components, 'documentationText')) {
        const display = findField(form.components, 'display');
        (display ? display.components : form.components).push({
          type: 'textarea', input: true, key: 'documentationText',
          label: 'Hilfe in diesem Formular',
          description: 'Formularbezogene Erklärung; wird im Customizing als Komponentenhilfe angezeigt.',
          weight: 154, validate: { maxLength: 4000 }
        });
      }
      if (!findField(form.components, 'documentation')) {
        const display = findField(form.components, 'display');
        const fields = display ? display.components : form.components;
        fields.push({
          type: 'textfield',
          input: true,
          key: 'documentation',
          label: 'Dokumentations-URL',
          description: 'Optional: HTTP-/HTTPS-URL oder relativer Pfad zur OZG-Dokumentation.',
          weight: 155,
          validate: { required: false },
        });
      }
      return form;
    };
    wrappedEditForms.add(editForm);
    ComponentClass.editForm = editForm;
  });
}

export function resolveDocumentationUrl(value, baseUrl) {
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const url = new URL(value.trim(), baseUrl);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
  } catch (error) {
    return null;
  }
}

export function createDocumentationAttachHook(baseUrl) {
  return (element, instance) => {
    const previous = helpElements.get(instance);
    if (previous) previous.remove();
    helpElements.delete(instance);

    const component = instance.component;
    if (instance.builderMode || instance.previewMode || !component ||
      !supportsDocumentation(component.type, Formio.Components.components[component.type])) return;
    const href = resolveDocumentationUrl(component.documentation, baseUrl);
    if (!href) return;

    const link = element.ownerDocument.createElement('a');
    const title = `Dokumentation: ${component.label || component.title || component.key || component.type}`;
    link.href = href;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.title = title;
    link.setAttribute('aria-label', title);
    link.setAttribute('data-noattach', 'true');
    link.className = 'ozg-component-documentation';
    link.style.cssText = 'display:inline-block;margin:0.25rem;line-height:1;';
    // Bootstrap's question-circle, matching the existing form-wide help icon.
    link.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/><path d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.81a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.978 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.749 2.286zM6.81 11.49c0 .533.425.927.965.927.559 0 .978-.394.978-.927 0-.552-.42-.94-.978-.94-.54 0-.965.388-.965.94z"/></svg>';
    // Append outside labels, buttons and grid-owned contents; no click handlers.
    element.appendChild(link);
    helpElements.set(instance, link);
  };
}

export function createDocumentationHooks(baseUrl) {
  const attachComponent = createDocumentationAttachHook(baseUrl);
  return {
    attachComponent,
    // Wizard renders page children directly, without attaching the page panel.
    // Its component is the current panel; attachWebform runs on every page redraw.
    attachWebform: (element, form) => {
      if (form.pages) attachComponent(element, form);
    },
  };
}
